import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Section = styled.section`
  padding: 80px 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 1rem;
  color: #1f2937;

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  font-size: 1.25rem;
  color: #6b7280;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const DiscussionNote = styled(motion.div)`
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  border: 1px solid #f59e0b;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 3rem;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  p {
    margin: 0;
    color: #92400e;
    font-weight: 500;
    font-size: 1rem;
  }

  i {
    color: #f59e0b;
    margin-right: 0.5rem;
  }
`;

const PrioritySection = styled.div`
  margin-bottom: 4rem;
`;

const PriorityTitle = styled(motion.h3)`
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  color: ${props => {
    switch(props.priority) {
      case 1: return '#dc2626';
      case 2: return '#d97706';
      case 3: return '#059669';
      default: return '#1f2937';
    }
  }};

  i {
    font-size: 1.5rem;
  }
`;

const RecommendationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RecommendationCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  border: 1px solid #e5e7eb;
  border-left: 4px solid ${props => {
    switch(props.priority) {
      case 1: return '#dc2626';
      case 2: return '#d97706';
      case 3: return '#059669';
      default: return '#e5e7eb';
    }
  }};
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const CardTitle = styled.h4`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  flex: 1;
  margin: 0;
`;

const EffortTag = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => {
    switch(props.effort) {
      case 'low': return '#d1fae5';
      case 'medium': return '#fef3c7';
      case 'high': return '#fef2f2';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch(props.effort) {
      case 'low': return '#059669';
      case 'medium': return '#d97706';
      case 'high': return '#dc2626';
      default: return '#6b7280';
    }
  }};
`;

const CardDescription = styled.p`
  color: #6b7280;
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const ActionItems = styled.ul`
  list-style: none;
  margin-bottom: 1.5rem;
  padding: 0;

  li {
    padding: 0.5rem 0;
    color: #374151;
    position: relative;
    padding-left: 1.5rem;

    &::before {
      content: '✓';
      position: absolute;
      left: 0;
      color: #059669;
      font-weight: 600;
    }
  }
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
  font-size: 0.875rem;
  color: #6b7280;
`;

const Timeline = styled.span`
  font-weight: 500;
`;

const Owner = styled.span`
  font-style: italic;
`;

const Recommendations = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const recommendations = [
    {
      priority: 1,
      title: 'Implement QR Code Feedback System',
      effort: 'low',
      description: 'Create QR code-based evaluation system for each Tech Week event venue to collect attendee feedback and provide speakers with performance feedback.',
      actionItems: [
        'Design standardized QR code posters',
        'Develop simple survey questions (1-3 questions)',
        'Create automated survey generation system',
        'Pilot with pre-Tech Week activities'
      ],
      timeline: '2-3 weeks',
      owner: 'Brandon Hamilton'
    },
    {
      priority: 1,
      title: 'Outsource Registration System',
      effort: 'medium',
      description: 'Arrange for outsourced registration services with on-the-spot name tag printing to streamline check-in process.',
      actionItems: [
        'Research registration service providers',
        'Implement on-demand name tag printing',
        'Eliminate pre-printed badge system',
        'Train volunteers on new system'
      ],
      timeline: '4-6 weeks',
      owner: 'Brandon Hamilton'
    },
    {
      priority: 1,
      title: 'Form Diversity Committee',
      effort: 'medium',
      description: 'Create diversity-focused Tech Week committee with Urban League, NAACP, Hispanic Chamber, Asian organizations, and community hubs.',
      actionItems: [
        'Coordinate with Andrea and Brandon for approval',
        'Recruit committee members from diverse organizations',
        'Plan diversity-focused events for 2026',
        'Increase representation in programming'
      ],
      timeline: '6-8 weeks',
      owner: 'TaRita Johnson'
    },
    {
      priority: 1,
      title: 'Create University Student Day',
      effort: 'medium',
      description: 'University of Michigan has expressed interest in joining Tech Week. Coordinate with Raul to connect with MSU contacts and create a dedicated "Student Day" that aligns with university visits.',
      actionItems: [
        'Connect with University of Michigan contacts',
        'Coordinate with Raul for MSU introductions',
        'Design student-focused programming and events',
        'Align student day timing with university schedules'
      ],
      timeline: '6-8 weeks',
      owner: 'Raul / Brandon Hamilton'
    },
    {
      priority: 2,
      title: 'Plan February/March Info Event',
      effort: 'medium',
      description: 'Organize public event (happy hour format) for February or March 2026 to recruit new participants and create pipeline entry point.',
      actionItems: [
        'Secure venue at The Right Place or community location',
        'Plan event format and agenda',
        'Invite engagement committee members and past hosts',
        'Create promotional materials'
      ],
      timeline: '8-10 weeks',
      owner: 'Brandon Hamilton'
    },
    {
      priority: 2,
      title: 'Add Partnership Contact System',
      effort: 'low',
      description: 'Create contact system on Tech Week website for year-round partnership inquiries (speaking, hosting, sponsoring).',
      actionItems: [
        'Design partnership contact page',
        'Implement contact information on Tech Week website',
        'Create matching system for speakers and venues',
        'Establish year-round engagement process'
      ],
      timeline: '4-6 weeks',
      owner: 'Brandon Hamilton'
    },
    {
      priority: 2,
      title: 'Establish Tech Week Hub Venue',
      effort: 'medium',
      description: 'Secure central hub location where attendees can gather, see event boards, and have meeting place throughout the week.',
      actionItems: [
        'Evaluate Bamboo as potential hub location',
        'Plan hub layout and amenities',
        'Coordinate with venue for week-long access',
        'Design event information displays'
      ],
      timeline: '6-8 weeks',
      owner: 'Brandon Hamilton'
    },
    {
      priority: 3,
      title: 'Explore Bus Wrap Sponsorship',
      effort: 'high',
      description: 'Investigate wrapping Grand Rapids bus with Tech Week branding for 1-2 months and route to events during the week.',
      actionItems: [
        'Research bus wrap costs and logistics',
        'Coordinate with The Rapid for routing',
        'Design Tech Week bus wrap graphics',
        'Plan promotional campaign around bus'
      ],
      timeline: '12-16 weeks',
      owner: 'Brandon Hamilton'
    },
    {
      priority: 3,
      title: 'Partner with Urban League Pitch Event',
      effort: 'high',
      description: 'Coordinate with Urban League to shift their $100,000+ pitch event to align with Tech Week timing.',
      actionItems: [
        'Reach out to Urban League leadership',
        'Plan event timing alignment',
        'Explore funding partnership opportunities',
        'Coordinate with community partners'
      ],
      timeline: '16-20 weeks',
      owner: 'Brandon Hamilton'
    }
  ];

  const priorityGroups = {
    1: { title: 'Priority 1: Immediate Implementation (Q1 2026)', icon: 'fas fa-exclamation-circle' },
    2: { title: 'Priority 2: Early Planning (Q2 2026)', icon: 'fas fa-clock' },
    3: { title: 'Priority 3: Strategic Implementation (Q3-Q4 2026)', icon: 'fas fa-calendar-check' }
  };

  return (
    <Section id="recommendations">
      <Container>
        <SectionTitle
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          2026 Recommendations
        </SectionTitle>
        
        <SectionSubtitle
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Prioritized action items for the Engagement Committee
        </SectionSubtitle>
        
        <DiscussionNote
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <p>
            <i className="fas fa-comments"></i>
            These recommendations and timelines are provided for committee discussion and planning purposes. 
            Final decisions will be made through committee consensus.
          </p>
        </DiscussionNote>
        
        {Object.entries(priorityGroups).map(([priority, group]) => (
          <PrioritySection key={priority}>
            <PriorityTitle
              priority={parseInt(priority)}
              initial={{ opacity: 0, x: -30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <i className={group.icon}></i>
              {group.title}
            </PriorityTitle>
            
            <RecommendationsGrid>
              {recommendations
                .filter(rec => rec.priority === parseInt(priority))
                .map((recommendation, index) => (
                  <RecommendationCard
                    key={index}
                    priority={recommendation.priority}
                    initial={{ opacity: 0, y: 30 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <CardHeader>
                      <CardTitle>{recommendation.title}</CardTitle>
                      <EffortTag effort={recommendation.effort}>
                        {recommendation.effort} Effort
                      </EffortTag>
                    </CardHeader>
                    
                    <CardDescription>{recommendation.description}</CardDescription>
                    
                    <ActionItems>
                      {recommendation.actionItems.map((item, itemIndex) => (
                        <li key={itemIndex}>{item}</li>
                      ))}
                    </ActionItems>
                    
                    <CardFooter>
                      <Timeline>Timeline: {recommendation.timeline}</Timeline>
                      <Owner>Owner: {recommendation.owner}</Owner>
                    </CardFooter>
                  </RecommendationCard>
                ))}
            </RecommendationsGrid>
          </PrioritySection>
        ))}
      </Container>
    </Section>
  );
};

export default Recommendations;
